import React, { useEffect, useRef } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import {
  Copy,
  ExternalLink,
  Facebook,
  Linkedin,
  Link2,
  Mail,
  MessageCircle,
  Twitter,
  X,
} from 'lucide-react';
import { buildPlatformShareLinks, copySharePost, copyShareUrl, getSharePayload } from '../utils/shareUtils';
import { useLanguage } from '../context/LanguageContext';

const FOCUSABLE_SELECTOR = [
  'button:not([disabled])',
  '[href]',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(', ');

function getFocusableElements(container) {
  if (!container) return [];

  return Array.from(container.querySelectorAll(FOCUSABLE_SELECTOR)).filter(
    (element) => !element.hasAttribute('disabled') && element.getAttribute('aria-hidden') !== 'true'
  );
}

const platformIconMap = {
  whatsapp: MessageCircle,
  x: Twitter,
  linkedin: Linkedin,
  facebook: Facebook,
  email: Mail,
};

const platformLabelByLocale = {
  en: {
    whatsapp: 'WhatsApp',
    x: 'X',
    linkedin: 'LinkedIn',
    facebook: 'Facebook',
    email: 'Email',
  },
  de: {
    whatsapp: 'WhatsApp',
    x: 'X',
    linkedin: 'LinkedIn',
    facebook: 'Facebook',
    email: 'E-Mail',
  },
  es: {
    whatsapp: 'WhatsApp',
    x: 'X',
    linkedin: 'LinkedIn',
    facebook: 'Facebook',
    email: 'Correo',
  },
};

const shareSheetCopy = {
  en: {
    copied: 'Post text copied to clipboard.',
    linkCopied: 'Link copied to clipboard.',
    copyUnavailable: 'Copy unavailable. Copy text manually.',
    linkCopyUnavailable: 'Link copy unavailable. Copy URL manually.',
    copyError: 'Could not copy post text right now.',
    linkCopyError: 'Could not copy link right now.',
    closeShareOptions: 'Close share options',
    shareOptions: 'Share options',
    shareStory: 'Share this story',
    choosePlatform: 'Choose your platform and share instantly.',
    copyReadyPost: 'Copy ready-to-post text',
    copyLink: 'Copy link',
  },
  de: {
    copied: 'Beitragstext in die Zwischenablage kopiert.',
    linkCopied: 'Link in die Zwischenablage kopiert.',
    copyUnavailable: 'Kopieren nicht verfuegbar. Bitte manuell kopieren.',
    linkCopyUnavailable: 'Link-Kopie nicht verfuegbar. Bitte URL manuell kopieren.',
    copyError: 'Der Beitragstext konnte gerade nicht kopiert werden.',
    linkCopyError: 'Der Link konnte gerade nicht kopiert werden.',
    closeShareOptions: 'Teilen-Optionen schliessen',
    shareOptions: 'Teilen-Optionen',
    shareStory: 'Diese Story teilen',
    choosePlatform: 'Waehle deine Plattform und teile sofort.',
    copyReadyPost: 'Fertigen Beitragstext kopieren',
    copyLink: 'Link kopieren',
  },
  es: {
    copied: 'Texto de la publicacion copiado al portapapeles.',
    linkCopied: 'Enlace copiado al portapapeles.',
    copyUnavailable: 'Copia no disponible. Copia el texto manualmente.',
    linkCopyUnavailable: 'Copia de enlace no disponible. Copia la URL manualmente.',
    copyError: 'No se pudo copiar el texto en este momento.',
    linkCopyError: 'No se pudo copiar el enlace en este momento.',
    closeShareOptions: 'Cerrar opciones para compartir',
    shareOptions: 'Opciones para compartir',
    shareStory: 'Compartir esta historia',
    choosePlatform: 'Elige tu plataforma y comparte al instante.',
    copyReadyPost: 'Copiar texto listo para publicar',
    copyLink: 'Copiar enlace',
  },
};

export default function ShareSheet({ open, onClose, payload, onStatus }) {
  const { locale } = useLanguage();
  const copy = shareSheetCopy[locale] || shareSheetCopy.en;
  const sharePayload = getSharePayload(payload);
  const platforms = buildPlatformShareLinks(sharePayload);
  const dialogRef = useRef(null);
  const closeButtonRef = useRef(null);
  const lastFocusedRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;

    const previousOverflow = document.body.style.overflow;
    lastFocusedRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    document.body.style.overflow = 'hidden';
    closeButtonRef.current?.focus();

    const onDialogKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose?.();
        return;
      }

      if (event.key !== 'Tab') return;

      const focusable = getFocusableElements(dialogRef.current);
      if (!focusable.length) {
        event.preventDefault();
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;
      const activeInsideDialog = dialogRef.current?.contains(active);

      if (!activeInsideDialog) {
        event.preventDefault();
        first.focus();
        return;
      }

      if (event.shiftKey && active === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    };

    window.addEventListener('keydown', onDialogKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', onDialogKeyDown);
      lastFocusedRef.current?.focus?.();
      lastFocusedRef.current = null;
    };
  }, [open, onClose]);

  const handleCopy = async () => {
    try {
      const copied = await copySharePost(sharePayload);

      if (copied) {
        onStatus?.(copy.copied);
        onClose?.();
        return;
      }

      onStatus?.(copy.copyUnavailable);
    } catch {
      onStatus?.(copy.copyError);
    }
  };

  const handleCopyLink = async () => {
    try {
      const copied = await copyShareUrl(sharePayload);

      if (copied) {
        onStatus?.(copy.linkCopied);
        onClose?.();
        return;
      }

      onStatus?.(copy.linkCopyUnavailable);
    } catch {
      onStatus?.(copy.linkCopyError);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.button
            type="button"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 z-[70] bg-[#020817]/68 backdrop-blur-[2px]"
            aria-label={copy.closeShareOptions}
          />

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[71] flex items-end sm:items-center justify-center p-4 pointer-events-none"
          >
            <motion.div
              ref={dialogRef}
              initial={{ y: 24, opacity: 0, scale: 0.98 }}
              animate={{ y: 0, opacity: 1, scale: 1 }}
              exit={{ y: 16, opacity: 0, scale: 0.98 }}
              transition={{ duration: 0.2 }}
              className="pointer-events-auto w-full max-w-md rounded-2xl border border-[#9eb6e3]/80 dark:border-[#5d76a7]/55 bg-[#f8fbff] dark:bg-[#101a31] ring-1 ring-white/55 dark:ring-white/10 shadow-[0_24px_80px_rgba(7,14,29,0.62)] p-5"
              role="dialog"
              aria-modal="true"
              aria-labelledby="share-sheet-title"
              data-disable-slide-nav="true"
            >
              <div className="flex items-start justify-between gap-3 mb-4">
                <div>
                  <h3 id="share-sheet-title" className="text-lg font-bold text-[#1f3056] dark:text-[#edf3ff]">{copy.shareStory}</h3>
                  <p className="text-xs text-[#4f628d] dark:text-[#bec8e0] mt-1">
                    {copy.choosePlatform}
                  </p>
                </div>
                <button
                  ref={closeButtonRef}
                  type="button"
                  onClick={onClose}
                  className="rounded-lg p-1.5 text-[#445a88] dark:text-[#dbe5ff] hover:bg-[#e0eafe] dark:hover:bg-[#263556] transition"
                  aria-label={copy.closeShareOptions}
                >
                  <X size={16} />
                </button>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {platforms.map((platform) => {
                  const Icon = platformIconMap[platform.key] || ExternalLink;
                  const platformLabels = platformLabelByLocale[locale] || platformLabelByLocale.en;
                  const externalTarget = platform.key === 'email' ? undefined : '_blank';
                  const externalRel = platform.key === 'email' ? undefined : 'noopener noreferrer';

                  return (
                    <a
                      key={platform.key}
                      href={platform.href}
                      target={externalTarget}
                      rel={externalRel}
                      onClick={onClose}
                      className="flex items-center justify-between gap-2 rounded-xl border border-[#b3c6ea] dark:border-[#536b98] px-3 py-2.5 text-sm text-[#203057] dark:text-[#e8efff] bg-[#f1f6ff] dark:bg-[#1c2a47] hover:bg-[#e0ebff] dark:hover:bg-[#273758] transition"
                    >
                      <span className="inline-flex items-center gap-2 font-medium">
                        <Icon size={15} />
                        {platformLabels[platform.key] || platform.label}
                      </span>
                      <ExternalLink size={13} className="opacity-70" />
                    </a>
                  );
                })}
              </div>

              <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={handleCopy}
                  className="w-full rounded-xl border border-[#b3c6ea] dark:border-[#536b98] px-3 py-2.5 text-sm font-medium text-[#203057] dark:text-[#e8efff] bg-[#ecf3ff] dark:bg-[#1a2743] hover:bg-[#dce8ff] dark:hover:bg-[#263556] transition inline-flex items-center justify-center gap-2"
                >
                  <Copy size={15} /> {copy.copyReadyPost}
                </button>
                <button
                  type="button"
                  onClick={handleCopyLink}
                  className="w-full rounded-xl border border-[#b3c6ea] dark:border-[#536b98] px-3 py-2.5 text-sm font-medium text-[#203057] dark:text-[#e8efff] bg-[#ecf3ff] dark:bg-[#1a2743] hover:bg-[#dce8ff] dark:hover:bg-[#263556] transition inline-flex items-center justify-center gap-2"
                >
                  <Link2 size={15} /> {copy.copyLink}
                </button>
              </div>
            </motion.div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
